import {Box, Button, Checkbox, Dialog, DialogActions, DialogContent, styled, Table, TableBody, TableCell, TableHead, TableRow} from '@mui/material';
import Typography from '@mui/material/Typography';
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import ConfirmationWrapper from '../../Components/ConfirmationWrapper';
import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import {useGetDietaryNeedsQuery, useManageDietaryNeedsMutation} from '../../../../../api/services/studentService';
import {useGetDietaryNeedOptionsQuery} from '../../../../../api/services/lookupsService';
import {useGetProfileDescriptionQuery} from '../../../../../api/services/schoolManagementService';
import {DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS, PROFILE_SECTIONS_KEYS} from '../../../../../constants/ProfileSectionsKeys';
import formatProfileDescription from '../../../../../utility/profileDescriptionFormatter';
import {getHisHerPronoun} from '../../../../../utility/pronounUtil';

// Maria, 29 October 2026: the school's name, not 'the school' (AD-2816)
export const NO_DIETARY_NEEDS_LINE = 'Please tick this box if @StudentName has no dietary needs that @SchoolName needs to be aware of.';

// AD-2823 (Maria, 17, 29 and 30 October 2026): the five Dietary Need Types, ticked under each ticked dietary need.
// Admicity only; they are not sent to SIMS. Maria, 30 October 2026: 'Allergy' on one line of the heading and the kind of
// allergy on the line beneath it.
export const DIETARY_NEED_TYPES = [
    {key: 'allergyAnaphylactic', label: 'Allergy\nAnaphylactic'},
    // a hyphen that never breaks, and a break allowed only after the slash
    {key: 'allergyNonAnaphylactic', label: 'Allergy\nNon‑Anaphylactic'},
    {key: 'intolerance', label: 'Intolerance'},
    {key: 'religiousCulturalObservation', label: 'Religious/​Cultural'},
    {key: 'dietaryPreference', label: 'Preference'}
];

// Maria, 30 October 2026: the table fits every screen. The Dietary Need column takes whatever space is left and its
// names wrap; the type columns are narrower on a tablet, and on a phone their headings read upwards.
const NEED_COLUMN = {px: {xs: 1, md: 2}};
// Maria, 30 October 2026: each type column is only as wide as its heading, and the Dietary Need column takes the spare
// space, so a name only wraps once no column has any spare space left
const TYPE_COLUMN = {width: '1%', px: {xs: 0.25, sm: 1, md: 2}};
const TYPE_HEADING = {
    ...TYPE_COLUMN,
    verticalAlign: 'bottom',
    '& > span': {
        display: 'inline-block',
        // Maria, 30 October 2026: on a smaller screen 'Religious/Cultural' may break at the slash; nothing else breaks
        whiteSpace: {xs: 'nowrap', sm: 'pre-wrap', lg: 'pre'},
        writingMode: {xs: 'vertical-rl', sm: 'horizontal-tb'},
        transform: {xs: 'rotate(180deg)', sm: 'none'}
    }
};

const typesOf = option => Object.fromEntries(DIETARY_NEED_TYPES.map(({key}) => [key, !!option?.[key]]));

export const DietaryNeedOrFoodAllergyItem = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 2
});

const DietaryNeeds = ({readonly, onConfirmSuccess}) => {
    const {studentId} = useParams();
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [deselectedOptions, setDeselectedOptions] = useState([]);
    const [isNotApplicable, setIsNotApplicable] = useState(undefined);
    const [types, setTypes] = useState({});
    // Maria, 30 October 2026: unticking the last type asks whether to remove the dietary need
    const [removing, setRemoving] = useState(null);
    const selectedProfile = useSelector(state => state.profilesInfo.selectedProfile);
    const currentSchool = useSelector(state => state.profilesInfo.currentSchool);
    const [manageDietaryNeeds] = useManageDietaryNeedsMutation();
    const {
        data: dietaryNeeds = [],
        refetch: refetchDietaryNeedsData
    } = useGetDietaryNeedsQuery(studentId);
    const {
        data: optionData = [],
        refetch: refetchDietaryNeedOptions
    } = useGetDietaryNeedOptionsQuery();
    // AD-2696: the school's own header box from the SIMS Lookups page, shown above the list.
    const {data: descriptionData} = useGetProfileDescriptionQuery(PROFILE_SECTIONS_KEYS.dietaryNeeds);
    const dietaryDescription = (descriptionData?.value ??
        DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.dietaryNeeds] ?? '').trim();

    useEffect(() => {
        if (dietaryNeeds?.options) {
            setSelectedOptions(dietaryNeeds?.options.map(x => x.optionId));
            setTypes(Object.fromEntries(dietaryNeeds.options.map(x => [x.optionId, typesOf(x)])));
            setIsNotApplicable(dietaryNeeds.isNotApplicable);
        }
    }, [dietaryNeeds]);

    const handleSelect = (event) => {
        const {checked, value} = event.target;
        const optionId = parseInt(value);

        if (checked) {
            setSelectedOptions([...selectedOptions, optionId]);
            setDeselectedOptions(deselectedOptions.filter(id => id !== optionId));
        } else {
            setDeselectedOptions([...deselectedOptions, optionId]);
            setSelectedOptions(selectedOptions.filter(id => id !== optionId));
            setTypes({...types, [optionId]: typesOf(null)});
        }
    };

    // Maria, 30 October 2026: ticking a type ticks the dietary need on that row, so it is saved and synced to SIMS.
    // Unticking the last type leaves the need ticked; the need's own tick removes it and its types.
    const handleTypeChange = (optionId, key, checked, name) => {
        const updated = {...typesOf(types[optionId]), [key]: checked};
        setTypes({...types, [optionId]: updated});
        if (checked && !selectedOptions.includes(optionId)) {
            setSelectedOptions([...selectedOptions, optionId]);
            setDeselectedOptions(deselectedOptions.filter(id => id !== optionId));
        }
        if (!checked && !DIETARY_NEED_TYPES.some(type => updated[type.key])) {
            setRemoving({optionId, name});
        }
    };

    const removeNeed = optionId => {
        setDeselectedOptions([...deselectedOptions, optionId]);
        setSelectedOptions(selectedOptions.filter(id => id !== optionId));
        setTypes({...types, [optionId]: typesOf(null)});
        setRemoving(null);
    };

    const typesChanged = () => selectedOptions.some(optionId => {
        const saved = dietaryNeeds?.options?.find(v => v.optionId === optionId);
        return DIETARY_NEED_TYPES.some(({key}) => !!types[optionId]?.[key] !== !!saved?.[key]);
    });

    const handleSave = async () => {
        const body = {
            types: selectedOptions.map(optionId => ({optionId, ...typesOf(types[optionId])})),
            selectedOptions: selectedOptions.filter(x => !dietaryNeeds?.options?.some(v => v.optionId === x)),
            deselectedOptions: deselectedOptions.filter(x => dietaryNeeds?.options?.some(v => v.optionId === x)),
            isNotApplicable,
            studentId
        };

        await manageDietaryNeeds({studentId, body});
    };

    const isEdited = () => {
        return selectedOptions.filter(x => !dietaryNeeds?.options?.some(v => v.optionId === x)).length !== 0 ||
            deselectedOptions.filter(x => dietaryNeeds?.options?.some(v => v.optionId === x)).length !== 0 || isNotApplicable !== dietaryNeeds.isNotApplicable ||
            typesChanged();
    };

    return (
        dietaryNeeds && <ConfirmationWrapper
                readonly={readonly}
                confirmData={{
                    confirmMessage: `I confirm that the information in the dietary needs section of ${selectedProfile.name} ${selectedProfile.surname}'s student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
                    confirmedAt: dietaryNeeds.confirmedAt,
                    confirmedBy: dietaryNeeds.confirmedBy,
                    enableSaveButton: isEdited() && (selectedOptions.length !== 0 || isNotApplicable),
                    enableConfirmButton: selectedOptions.length !== 0 || isNotApplicable,
                    studentId,
                    type: STUDENT_PROFILE_CONFIRMATION_TYPES.Dietary,
                    handleSave,
                    onConfirmSuccess: async () => {
                        await onConfirmSuccess();
                        await refetchDietaryNeedsData();
                        await refetchDietaryNeedOptions();
                    },
                    confirmButtonText: 'Confirm Dietary Needs'
                }}
                actionsBelow
                heading={(
                    <Box sx={{flex: '1 1 360px', minWidth: 0}}>
                        {dietaryDescription && (
                            <Typography sx={{whiteSpace: 'pre-line'}} data-testid="dietary-needs-description">
                                {formatProfileDescription(dietaryDescription, {
                                    schoolName: currentSchool?.name,
                                    studentName: selectedProfile.name,
                                    hisHer: getHisHerPronoun(selectedProfile.gender)
                                })}
                            </Typography>
                        )}
                        {/* Maria, 18 October 2026: the 'none' tick in the space beside the confirm button */}
                    <Box component="label" sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 1.5}}>
                        <Checkbox
                            sx={{width: '25px', height: '25px'}}
                            value={isNotApplicable}
                            checked={!!isNotApplicable}
                            disabled={readonly || (selectedOptions.length > 0 && !isNotApplicable)}
                            onChange={(e) => setIsNotApplicable(e.target.checked)}
                        />
                        <Typography>
                            {formatProfileDescription(NO_DIETARY_NEEDS_LINE, {
                                schoolName: currentSchool?.name,
                                studentName: selectedProfile.name
                            })}
                        </Typography>
                    </Box>
                    </Box>
                )}>
                {/* Maria, 18 October 2026: the list stays visible when 'no dietary needs' is ticked; its boxes are
                    then greyed and cannot be ticked. Maria, 30 October 2026: no fixed sentence above the table; the
                    school's own text from the Lookups page is the introduction. */}
                {/* Maria, 30 October 2026 (AD-2823): a table, one row for each dietary need and a column for each Dietary
                    Need Type */}
                {/* Maria, 30 October 2026: the same style as the Contacts and Forms tables, with the light green header */}
                <Box sx={{overflowX: 'auto', mt: 2, mb: 1.5, border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 1, backgroundColor: '#fff', color: isNotApplicable ? '#9aa5b1' : 'inherit'}}>
                    <Table size="small" data-testid="dietary-needs-table" sx={{width: '100%', '& td, & th': {borderColor: 'rgba(0, 0, 0, 0.12)', color: 'inherit'}, '& tbody td': {py: 0.25}, '& tbody tr:last-of-type td': {borderBottom: 0}}}>
                        <TableHead>
                            <TableRow sx={{'& th': {backgroundColor: '#e3f5f1', fontWeight: 600, py: 1, lineHeight: 1.25, verticalAlign: 'middle'}}}>
                                <TableCell sx={{...NEED_COLUMN, verticalAlign: 'bottom'}}>Dietary Need</TableCell>
                                {DIETARY_NEED_TYPES.map(type => (
                                    <TableCell key={type.key} align="center" sx={TYPE_HEADING}>
                                        <span>{type.label}</span>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {optionData.options?.map(field => {
                                const name = field.title || field.description;
                                const ticked = selectedOptions?.some(id => id === field.id);
                                return (
                                    <TableRow key={field.id} data-testid={`dietary-need-row-${field.id}`}>
                                        <TableCell sx={NEED_COLUMN}>
                                            <Box component="label" sx={{display: 'flex', alignItems: 'center', gap: 1, cursor: readonly ? 'default' : 'pointer'}}>
                                                <Checkbox
                                                    sx={{width: '25px', height: '25px'}}
                                                    value={field.id}
                                                    checked={ticked}
                                                    onChange={handleSelect}
                                                    disabled={readonly || !!isNotApplicable}
                                                    inputProps={{'aria-label': name}}
                                                />
                                                <Box>
                                                    <Typography sx={{color: 'inherit', overflowWrap: 'break-word'}}>{name}</Typography>
                                                    {field.description && field.title
                                                        ? <Typography variant="body2" color="text.secondary">{field.description}</Typography>
                                                        : null}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        {DIETARY_NEED_TYPES.map(type => (
                                            <TableCell key={type.key} align="center" sx={TYPE_COLUMN}>
                                                <Checkbox
                                                    sx={{width: '25px', height: '25px'}}
                                                    checked={!!types[field.id]?.[type.key]}
                                                    onChange={e => handleTypeChange(field.id, type.key, e.target.checked, name)}
                                                    disabled={readonly || !!isNotApplicable}
                                                    inputProps={{'aria-label': `${name}: ${type.label.replace('\n', ' ').replace('​', '').replace('‑', '-')}`}}
                                                />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
                <Dialog open={!!removing} onClose={() => setRemoving(null)} data-testid="remove-dietary-need">
                    <DialogContent>
                        <Typography>
                            Do you want to remove {removing?.name} from {selectedProfile.name}&apos;s dietary needs?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRemoving(null)}>Keep</Button>
                        <Button variant="contained" onClick={() => removeNeed(removing.optionId)}>Remove</Button>
                    </DialogActions>
                </Dialog>
            </ConfirmationWrapper>
    );
};

DietaryNeeds.propTypes = {
    readonly: PropTypes.bool,
    onConfirmSuccess: PropTypes.func,
    onDataLoading: PropTypes.func,
};
export default DietaryNeeds;